"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useSticky = void 0;
var _react = require("react");
var _layers = require("@site/pattern-library-utils/layers");
/* eslint-disable max-statements */
/* eslint-disable no-console */
/* eslint-disable complexity */

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/* Function to find imidiate parent element based on selectors */
const findParentElement = (element, belowHeaderElement) => {
  const parentSelectors = ['.swp-react', '[data-track-name="below_header"]'];
  const hasParent = parentSelectors.some(selector => element.parentElement?.closest(selector));
  const closestBelowHeader = element.closest('[data-track-name="below_header"]');
  const finalSelector = belowHeaderElement && belowHeaderElement.children.length > 1 ? parentSelectors.join(', ') : ':not(:last-child)';

  // If the element has a parent with the below_header data-track-name, return that parent for Storefront.
  if (element.matches('[data-track-name="below_header"]')) {
    return element;
  }
  if (!hasParent) {
    return null;
  }
  if (closestBelowHeader) {
    return closestBelowHeader;
  }
  return element.closest(finalSelector);
};

/**
 * Custom hook for managing sticky behavior of an element on the page-level
 * It detects custom parents and handles sticky positioning based on scroll events.
 *
 * @param {string} elementId The ID of the element to make sticky
 * @param {React.Ref} existingRef Optional existing ref to use for the element
 * @param {boolean} enableSticky Flag to enable or disable sticky behavior
 * @param {string} parentSelector Optional selector to find the parent element to make sticky
 * @returns {Object} An object containing the `isStickyState` state variable
 */
const useSticky = (elementId, existingRef = null, enableSticky = true, parentSelector = 'header') => {
  const localRef = (0, _react.useRef)(null);
  const ref = existingRef || localRef;
  const [isStickyState, setIsStickyState] = (0, _react.useState)(false);
  const [topValues, setTopValues] = (0, _react.useState)({});
  const isClient = typeof window !== 'undefined';
  const observerRef = (0, _react.useRef)(null);
  const isRecalculatingRef = (0, _react.useRef)(false);
  const belowHeaderElementRef = (0, _react.useRef)(null);

  /**
   * Initializes the belowHeaderElementRef.
   */
  const initializeBelowHeaderElement = (0, _react.useCallback)(() => {
    if (isClient) {
      belowHeaderElementRef.current = document.getElementById('below-header');
    }
  }, [isClient]);

  /**
   * Recalculates sticky orders to ensure they are sequential.
   */
  const recalculateStickyOrders = (0, _react.useCallback)(() => {
    if (!isClient) return;
    isRecalculatingRef.current = true;
    const allStickyElements = Array.from(document.querySelectorAll('[data-sticky-order]'));
    const uniqueElements = [...new Set(allStickyElements)];
    uniqueElements.sort((a, b) => {
      if (a === b) return 0;
      if (a.compareDocumentPosition(b) === Node.DOCUMENT_POSITION_FOLLOWING) {
        return -1;
      }
      return 1;
    });
    uniqueElements.forEach((element, index) => {
      const currentOrder = element.getAttribute('data-sticky-order');
      const newOrder = index.toString();
      if (currentOrder !== newOrder) {
        element.setAttribute('data-sticky-order', newOrder);
      }
    });
    isRecalculatingRef.current = false;
  }, [isClient]);

  /**
   * Calculates the top positions for all sticky elements based on their order.
   **/
  const calculateTopValue = (0, _react.useCallback)(() => {
    if (!isClient) return;
    try {
      recalculateStickyOrders();
      const allStickyElements = Array.from(document.querySelectorAll('[data-sticky-order]'));
      const sortedStickyElements = allStickyElements.sort((a, b) => {
        const orderA = parseInt(a.getAttribute('data-sticky-order'), 10);
        const orderB = parseInt(b.getAttribute('data-sticky-order'), 10);
        return orderA - orderB;
      });
      const topValuesMap = {};
      let cumulativeTop = 0;
      let anchorTop = null;
      sortedStickyElements.forEach((element, index) => {
        const stickyOrder = parseInt(element.getAttribute('data-sticky-order'), 10);
        element.setAttribute('data-sticky', 'false');
        element.style.willChange = 'position, top';
        element.style.position = 'sticky';
        if (element.getAttribute('id') !== 'anchor-link-nav') {
          if (index > 0) {
            const previousElement = sortedStickyElements[index - 1];
            cumulativeTop += previousElement.offsetHeight;
          }
          topValuesMap[stickyOrder] = cumulativeTop;
          element.style.top = `${cumulativeTop}px`;
        } else {
          if (anchorTop === null) {
            if (index > 0) {
              const previousElement = sortedStickyElements[index - 1];
              cumulativeTop += previousElement.offsetHeight;
            }
            anchorTop = cumulativeTop;
          }
          topValuesMap[stickyOrder] = anchorTop;
          element.style.top = `${anchorTop}px`;
        }
      });
      const belowHeaderElement = belowHeaderElementRef.current;
      if (belowHeaderElement && belowHeaderElement.children.length > 1) {
        const stickyChildren = Array.from(belowHeaderElement.querySelectorAll('[data-sticky-order]'));
        if (stickyChildren.length > 1) {
          stickyChildren.sort((a, b) => {
            return parseInt(a.getAttribute('data-sticky-order'), 10) - parseInt(b.getAttribute('data-sticky-order'), 10);
          });
          const firstStickyChild = stickyChildren[0];
          const firstStickyOrder = parseInt(firstStickyChild.getAttribute('data-sticky-order'), 10);
          const firstChildTopValue = topValuesMap[firstStickyOrder] || 0;
          belowHeaderElement.style.position = 'sticky';
          belowHeaderElement.style.top = `${firstChildTopValue}px`;
          belowHeaderElement.style.zIndex = _layers.knownZIndexes.secondaryNav;
        }
      }
      setTopValues(topValuesMap);
      return topValuesMap;
    } catch (error) {
      console.error('Error calculating top values:', error);
    }
  }, [isClient, recalculateStickyOrders]);

  /**
   * Assigns a sticky order to the provided element.
   * @param {HTMLElement} element - The element to assign a sticky order.
   */
  const assignStickyOrder = (0, _react.useCallback)(element => {
    if (!isClient || !element) return;
    try {
      const existingOrders = new Set(Array.from(document.querySelectorAll('[data-sticky-order]')).map(el => parseInt(el.getAttribute('data-sticky-order'), 10)));
      let stickyOrderIndex = 1;
      while (existingOrders.has(stickyOrderIndex)) {
        stickyOrderIndex++;
      }
      element.setAttribute('data-sticky-order', stickyOrderIndex.toString());
    } catch (error) {
      console.error('Error assigning sticky order:', error);
    }
  }, [isClient]);

  /**
   * Applies the initial sticky state to the target element based on scroll position.
   */
  const applyInitialSticky = (0, _react.useCallback)(() => {
    if (!ref.current || !enableSticky || ref.current.id === 'main') return;
    try {
      const isSecondaryNav = elementId === 'secondary-nav' || elementId === 'enhanced-secondary-nav';
      const isHeader = ref.current.matches(parentSelector) || ref.current.closest(parentSelector) !== null;
      let stickyTarget = ref.current;
      const stickyOrder = parseInt(ref.current.getAttribute('data-sticky-order'), 10) || 0;
      const currentTopValue = topValues[stickyOrder] || 0;
      if (isHeader) {
        stickyTarget = document.querySelector(parentSelector);
        stickyTarget.style.position = 'sticky';
        stickyTarget.style.top = `${currentTopValue}px`;
        stickyTarget.style.zIndex = _layers.knownZIndexes.primaryNav;
      } else {
        const parentElement = findParentElement(ref.current, belowHeaderElementRef.current);
        if (parentElement) {
          stickyTarget = parentElement;
          stickyTarget.style.position = 'sticky';
          stickyTarget.style.top = `${currentTopValue}px`;
          if (isHeader) {
            stickyTarget.style.zIndex = _layers.knownZIndexes.primaryNav;
          } else if (isSecondaryNav) {
            stickyTarget.style.zIndex = _layers.knownZIndexes.secondaryNav;
          } else {
            stickyTarget.style.zIndex = _layers.knownZIndexes.anchorLinkNav;
          }
          ref.current.style.position = '';
          ref.current.style.top = '';
        } else {
          stickyTarget = ref.current;
          stickyTarget.style.position = 'sticky';
          stickyTarget.style.top = `${currentTopValue}px`;
          stickyTarget.style.zIndex = _layers.knownZIndexes.anchorLinkNav;
        }
      }
      const rect = stickyTarget.getBoundingClientRect();
      if (rect.top <= (currentTopValue ?? 0)) {
        stickyTarget.setAttribute('data-sticky', 'true');
        setIsStickyState(true);
      } else {
        stickyTarget.setAttribute('data-sticky', 'false');
        setIsStickyState(false);
      }
    } catch (error) {
      console.error('Error applying initial sticky:', error);
    }
  }, [enableSticky, parentSelector, ref, topValues]);

  /**
   * Handles the scroll event to toggle sticky state based on scroll position.
   */
  const handleScroll = (0, _react.useCallback)(() => {
    if (!ref.current || !enableSticky || !ref.current.hasAttribute('data-sticky-order')) return;
    try {
      const rect = ref.current.getBoundingClientRect();
      const stickyOrder = parseInt(ref.current.getAttribute('data-sticky-order'), 10) || 0;
      const currentTopValue = topValues[stickyOrder] || 0;
      let stickyTarget = ref.current;
      const parentElement = findParentElement(ref.current, belowHeaderElementRef.current);
      if (parentElement) {
        stickyTarget = parentElement;
        stickyTarget.style.top = `${currentTopValue}px`;
        ref.current.style.position = '';
        ref.current.style.top = '';
      } else {
        stickyTarget = ref.current;
      }
      if (rect.top <= (currentTopValue ?? 0) && !isStickyState) {
        stickyTarget.style.position = 'sticky';
        stickyTarget.style.top = `${currentTopValue}px`;
        if (!parentElement) {
          stickyTarget.style.zIndex = elementId === 'secondary-nav' ? _layers.knownZIndexes.secondaryNav : _layers.knownZIndexes.anchorLinkNav;
        }
        stickyTarget.setAttribute('data-sticky', 'true');
        setIsStickyState(true);
      } else if (rect.top > (currentTopValue ?? 0) && isStickyState) {
        stickyTarget.style.position = 'sticky';
        stickyTarget.setAttribute('data-sticky', 'false');
        setIsStickyState(false);
      }
    } catch (error) {
      console.error('Error handling scroll event:', error);
    }
  }, [enableSticky, ref, isStickyState, topValues]);

  /**
   * Handles the resize event to recalculate top values and adjust sticky elements accordingly.
   */
  const handleResize = (0, _react.useCallback)(() => {
    const updatedTopValues = calculateTopValue();
    if (ref.current && enableSticky) {
      const stickyOrder = parseInt(ref.current.getAttribute('data-sticky-order'), 10);
      const parentElement = findParentElement(ref.current, belowHeaderElementRef.current);
      if (parentElement) {
        parentElement.style.top = `${updatedTopValues[stickyOrder] || 0}px`;
      } else {
        ref.current.style.top = `${updatedTopValues[stickyOrder] || 0}px`;
      }
    }
  }, [calculateTopValue, enableSticky, ref]);

  /**
   * Sets up event listeners for scroll and resize, and cleans them up on unmount.
   */
  (0, _react.useEffect)(() => {
    if (!isClient) return;
    initializeBelowHeaderElement();
    const debouncedHandleScroll = debounce(handleScroll, 10);
    const debouncedHandleResize = debounce(handleResize, 10);

    // Initial
    applyInitialSticky();
    window.addEventListener('scroll', debouncedHandleScroll);
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [applyInitialSticky, handleScroll, handleResize, isClient, initializeBelowHeaderElement]);

  /**
   * Assigns sticky order and calculates top values when enabling sticky or on initial mount.
   */
  (0, _react.useEffect)(() => {
    if (!isClient) return;
    if (enableSticky && ref.current) {
      assignStickyOrder(ref.current);
      requestAnimationFrame(calculateTopValue);
    } else if (ref.current) {
      ref.current.removeAttribute('data-sticky-order');
    }
  }, [enableSticky, assignStickyOrder, calculateTopValue, isClient, ref]);

  /**
   * Listen for mutations on the document to recalculate top values when sticky elements change.
   */
  (0, _react.useEffect)(() => {
    if (!isClient) return;
    const observerConfig = {
      childList: true,
      subtree: false,
      attributes: true,
      attributeFilter: ['data-sticky-order', 'style', 'class']
    };
    const handleMutation = mutations => {
      if (isRecalculatingRef.current) return;
      let shouldRecalculate = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const hasRelevantChange = Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes)).some(node => {
            if (node.nodeType !== Node.ELEMENT_NODE) return false;
            return node.hasAttribute('data-sticky-order') || node.querySelector('[data-sticky-order]') || node.classList.contains('swp-react');
          });
          if (hasRelevantChange) {
            shouldRecalculate = true;
            break;
          }
        } else if (mutation.type === 'attributes' && (mutation.attributeName === 'data-sticky-order' || mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
          shouldRecalculate = true;
          break;
        }
      }
      if (shouldRecalculate) {
        requestAnimationFrame(() => {
          calculateTopValue();
        });
      }
    };
    if (!observerRef.current) {
      observerRef.current = new MutationObserver(handleMutation);
    }
    const swpReactElements = Array.from(document.querySelectorAll('.swp-react'));
    const targetsToObserve = [document.body, document.querySelector('#header'), document.querySelector('#below-header'), ...swpReactElements.filter(el => el.querySelector('[data-sticky-order]')), ...swpReactElements.flatMap(el => Array.from(el.parentElement?.children || []).filter(sibling => sibling !== el && sibling.hasAttribute('data-sticky-order')))].filter(Boolean);
    targetsToObserve.forEach(target => {
      observerRef.current.observe(target, observerConfig);
    });
    calculateTopValue();
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [calculateTopValue, isClient]);
  return {
    isStickyState
  };
};
exports.useSticky = useSticky;